(function () {
  const SWIPE_THRESHOLD = 10;
  const HLS_JS_SRC = "https://cdn.jsdelivr.net/npm/hls.js@1";

  let hlsLoaderPromise = null;

  function loadHlsLibrary() {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (hlsLoaderPromise) return hlsLoaderPromise;

    hlsLoaderPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = HLS_JS_SRC;
      script.async = true;
      script.onload = () => resolve(window.Hls);
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return hlsLoaderPromise;
  }

  function canPlayNativeHls(video) {
    return Boolean(
      video.canPlayType("application/vnd.apple.mpegurl") ||
        video.canPlayType("application/x-mpegURL"),
    );
  }

  // Promote data-src / data-poster on a slide only when it becomes active.
  function loadLazySlideMedia(slide) {
    if (!slide || slide.dataset.slideMediaLoaded === "true") return;
    slide.dataset.slideMediaLoaded = "true";

    slide.querySelectorAll("img[data-src]").forEach((img) => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute("data-src");
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute("data-srcset");
      }
    });

    slide.querySelectorAll("video[data-card-video]").forEach((video) => {
      if (video.dataset.poster && !video.getAttribute("poster")) {
        video.setAttribute("poster", video.dataset.poster);
      }
    });
  }

  function bindCardVideoUI(video) {
    if (video.dataset.cardVideoUiBound === "true") return;
    video.dataset.cardVideoUiBound = "true";

    const slide = video.closest(".card__video-slide");
    if (!slide) return;

    const loadingBar = slide.querySelector(".card__video-loading__bar");

    const getBufferedRatio = () => {
      const { buffered, duration } = video;
      if (!buffered.length || !Number.isFinite(duration) || duration <= 0) {
        return null;
      }

      let end = 0;
      for (let i = 0; i < buffered.length; i += 1) {
        end = Math.max(end, buffered.end(i));
      }
      return Math.min(end / duration, 1);
    };

    const updateLoadingProgress = () => {
      if (!loadingBar) return;

      const ratio = getBufferedRatio();
      if (ratio === null) {
        slide.classList.add("is-video-loading--indeterminate");
        loadingBar.style.width = "";
        return;
      }

      slide.classList.remove("is-video-loading--indeterminate");
      loadingBar.style.width = `${Math.round(ratio * 100)}%`;
    };

    const showLoading = () => {
      slide.classList.add("is-video-loading");
      updateLoadingProgress();
    };

    const hideLoading = () => {
      slide.classList.remove("is-video-loading", "is-video-loading--indeterminate");
      if (loadingBar) loadingBar.style.width = "0";
    };

    const reveal = () => {
      slide.classList.add("is-video-playing");
      hideLoading();
    };

    const hide = () => {
      slide.classList.remove("is-video-playing");
      hideLoading();
    };

    video.addEventListener("loadstart", showLoading);
    video.addEventListener("waiting", showLoading);
    video.addEventListener("progress", updateLoadingProgress);
    video.addEventListener("canplay", updateLoadingProgress);
    video.addEventListener("playing", reveal);
    video.addEventListener("pause", hide);
    video.addEventListener("emptied", hide);

    video._cardVideoUI = { showLoading, hideLoading, updateLoadingProgress };
  }

  function attachHlsLoadingListeners(video) {
    const hls = video._hls;
    const ui = video._cardVideoUI;
    if (!hls || !ui || hls._cardLoadingBound) return;
    hls._cardLoadingBound = true;

    hls.on("hlsFragLoading", ui.showLoading);
    hls.on("hlsFragBuffered", ui.updateLoadingProgress);
    hls.on("hlsBufferAppended", ui.updateLoadingProgress);
  }

  // Lazily attach a media source (HLS preferred, MP4 fallback) on first use.
  function ensureVideoSource(video) {
    if (video.dataset.cardVideoSourced === "true") {
      return Promise.resolve();
    }
    video.dataset.cardVideoSourced = "true";

    video._cardVideoUI?.showLoading();

    const hlsSrc = video.dataset.hlsSrc;
    const mp4Src = video.dataset.mp4Src;

    if (hlsSrc && canPlayNativeHls(video)) {
      video.src = hlsSrc;
      return Promise.resolve();
    }

    if (hlsSrc) {
      return loadHlsLibrary()
        .then((Hls) => {
          if (Hls && Hls.isSupported()) {
            const hls = new Hls({ enableWorker: true });
            hls.loadSource(hlsSrc);
            hls.attachMedia(video);
            video._hls = hls;
            attachHlsLoadingListeners(video);
          } else if (mp4Src) {
            video.src = mp4Src;
          }
        })
        .catch(() => {
          if (mp4Src) video.src = mp4Src;
        });
    }

    if (mp4Src) {
      video.src = mp4Src;
    }
    return Promise.resolve();
  }

  function initCardVideoHover(picture) {
    if (picture.dataset.cardVideoInit === "true") return;

    const video = picture.querySelector(".card__video--hover");
    if (!video) return;

    picture.dataset.cardVideoInit = "true";

    const cardLink = picture.closest("a.card");
    if (!cardLink || !window.matchMedia("(hover: hover) and (min-width: 900px)").matches) {
      return;
    }

    const play = () => {
      const slide = video.closest(".card__video-slide");
      if (slide) loadLazySlideMedia(slide);

      video._cardVideoUI?.showLoading();
      ensureVideoSource(video).then(() => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      });
    };

    const pause = () => {
      video.pause();
      video.currentTime = 0;
    };

    cardLink.addEventListener("mouseenter", play);
    cardLink.addEventListener("mouseleave", pause);
    cardLink.addEventListener("focusin", play);
    cardLink.addEventListener("focusout", pause);
  }

  function initCardMediaTrack(track) {
    if (track.dataset.cardMediaInit === "true") return;
    track.dataset.cardMediaInit = "true";

    const picture = track.closest(".card__picture--dual");
    if (!picture) return;

    const dots = picture.querySelectorAll(".card__media-dot");
    const cardLink = picture.closest("a.card");
    const slides = track.querySelectorAll(".card__media-slide");

    const preloadUpcomingSlide = () => {
      if (!track.clientWidth || !slides[1]) return;
      if (track.scrollLeft > track.clientWidth * 0.08) {
        loadLazySlideMedia(slides[1]);
      }
    };

    const updateActiveSlide = () => {
      if (!track.clientWidth) return;
      const index = Math.round(track.scrollLeft / track.clientWidth);

      dots.forEach((dot, i) => {
        dot.classList.toggle("is-active", i === index);
      });

      slides.forEach((slide, i) => {
        if (i === index) {
          loadLazySlideMedia(slide);

          const video = slide.querySelector("video[data-card-video]");
          if (video) {
            video._cardVideoUI?.showLoading();
            ensureVideoSource(video).then(() => {
              const playPromise = video.play();
              if (playPromise !== undefined) playPromise.catch(() => {});
            });
          }
        } else {
          const video = slide.querySelector("video[data-card-video]");
          if (video) video.pause();
        }
      });
    };

    track.addEventListener("scroll", () => {
      preloadUpcomingSlide();
      updateActiveSlide();
    }, { passive: true });
    updateActiveSlide();

    // Desktop hover: preload secondary slide media before CSS reveals it.
    if (cardLink && window.matchMedia("(hover: hover) and (min-width: 900px)").matches) {
      cardLink.addEventListener("mouseenter", () => {
        slides.forEach((slide, i) => {
          if (i > 0) loadLazySlideMedia(slide);
        });
      });
    }

    if (!cardLink) return;

    let startX = 0;
    let startY = 0;
    let didSwipe = false;

    track.addEventListener(
      "touchstart",
      (e) => {
        const touch = e.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        didSwipe = false;
      },
      { passive: true },
    );

    track.addEventListener(
      "touchmove",
      (e) => {
        const touch = e.touches[0];
        const dx = Math.abs(touch.clientX - startX);
        const dy = Math.abs(touch.clientY - startY);
        if (dx > SWIPE_THRESHOLD && dx > dy) {
          didSwipe = true;
        }
      },
      { passive: true },
    );

    track.addEventListener(
      "touchend",
      () => {
        if (!didSwipe) return;
        cardLink.dataset.swiped = "true";
        window.setTimeout(() => {
          delete cardLink.dataset.swiped;
        }, 300);
      },
      { passive: true },
    );

    cardLink.addEventListener("click", (e) => {
      if (cardLink.dataset.swiped === "true") {
        e.preventDefault();
      }
    });
  }

  function initAll() {
    document.querySelectorAll("video[data-card-video]").forEach(bindCardVideoUI);
    document.querySelectorAll("[data-card-media-track]").forEach(initCardMediaTrack);
    document.querySelectorAll(".card__picture--has-video").forEach(initCardVideoHover);
  }

  let gridObserverTimer = null;

  function observeCollectionGrid() {
    const grid = document.getElementById("product-grid");
    if (!grid || grid._cardMediaObserver) return;

    const observer = new MutationObserver(() => {
      clearTimeout(gridObserverTimer);
      gridObserverTimer = setTimeout(initAll, 100);
    });

    observer.observe(grid, { childList: true, subtree: true });
    grid._cardMediaObserver = observer;
  }

  function setup() {
    initAll();
    observeCollectionGrid();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", setup);
  } else {
    setup();
  }

  document.addEventListener("shopify:section:load", setup);
  document.addEventListener("facets:change", setup);
})();
